import { useEffect, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useT, type MessageKey } from '@/i18n/I18nProvider';
import { usePermissionChecker } from '@/features/auth/permissions';
import { useAuthStore } from '@/features/auth/auth.store';
import { ClientBadge, NexopsWordmark } from '@/features/branding/BrandMark';
import { cn } from '@/shared/lib/cn';
import { accentForPath, hexToRgba } from '@/shared/lib/nav-accent';
import { useUiStore } from '@/shared/theme/ui.store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Tooltip,
} from '@/shared/ui';
import { buildMenu, filterNav, NAV_ITEMS, type NavItem } from './nav';
import { useNavBadges } from './useNavBadges';
import { appBuildTitle, appVersionLabel } from '@/shared/lib/app-version';

function useVisibleNav(): NavItem[] {
  const can = usePermissionChecker();
  const features = useAuthStore((s) => s.features);
  return useMemo(() => filterNav(NAV_ITEMS, can, features), [can, features]);
}

function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="absolute -right-1 -top-1 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-black text-white"
      style={{ boxShadow: '0 0 0 1.5px rgba(15,23,42,0.9)' }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** Pozycja menu w stylu ZUR OPS: kapsuła z ikoną w kolorze widoku, aktywna z poświatą. */
function NavEntry({
  item,
  collapsed,
  active,
  badge,
  onNavigate,
  asButton,
  expanded,
  onToggle,
}: {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  badge: number;
  onNavigate?: () => void;
  asButton?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const t = useT();
  const color = item.color ?? accentForPath(item.to).color;
  const cls = cn(
    'group relative flex w-full items-center gap-3 rounded-xl py-1.5 text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
    collapsed ? 'justify-center px-2' : 'px-2.5',
    active ? 'text-white' : 'text-sidebar-foreground hover:bg-white/[0.06] hover:text-white',
  );
  const style = active
    ? { background: hexToRgba(color, 0.14), boxShadow: `inset 0 0 0 1px ${hexToRgba(color, 0.27)}, 0 0 16px ${hexToRgba(color, 0.2)}` }
    : undefined;
  const pill = (
    <span
      className="relative flex size-7 shrink-0 items-center justify-center rounded-lg transition-all duration-150"
      style={active ? { background: color, boxShadow: `0 0 14px ${hexToRgba(color, 0.55)}` } : { background: hexToRgba(color, 0.12) }}
    >
      <item.icon className="size-4 transition-colors" style={{ color: active ? '#fff' : color }} />
      <Badge count={badge} />
    </span>
  );
  const label = !collapsed && <span className="flex-1 truncate text-left">{t(item.labelKey)}</span>;

  const body = asButton ? (
    <button type="button" onClick={onToggle} aria-expanded={expanded} className={cls} style={style}>
      {pill}
      {label}
      {!collapsed && <ChevronDown className={cn('size-3.5 opacity-60 transition-transform', !expanded && '-rotate-90')} />}
    </button>
  ) : (
    <NavLink to={item.to} end={item.to === '/'} onClick={onNavigate} className={cls} style={style}>
      {pill}
      {label}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip content={t(item.labelKey)} side="right">
      {body}
    </Tooltip>
  ) : (
    body
  );
}

/** Shared sidebar body (desktop rail + mobile drawer). */
export function SidebarContent({ collapsed = false, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const t = useT();
  const items = useVisibleNav();
  const badges = useNavBadges();
  const { pathname } = useLocation();
  // Aktywna jest pozycja (także z podmenu) z najdłuższym pasującym adresem — `/insurance` nie świeci razem z `/insurance/claims`.
  const activeTo = items
    .flatMap((i) => [i.to, ...(i.children ?? []).map((c) => c.to)])
    .filter((to) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`)))
    .sort((a, b) => b.length - a.length)[0];
  const sections = useMemo(() => buildMenu(items), [items]);

  return (
    <div className="dark flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className={cn('flex flex-col gap-3 border-b border-sidebar-border pb-3 pt-4', collapsed ? 'items-center px-2' : 'px-3.5')}>
        <NexopsWordmark collapsed={collapsed} onDark className={collapsed ? '' : 'px-0.5'} />
        <ClientBadge collapsed={collapsed} />
      </div>

      <nav aria-label={t('nav.main')} className={cn('flex-1 space-y-4 overflow-y-auto py-3', collapsed ? 'px-2' : 'px-3')}>
        {sections.map((section) => (
          <div key={section.key} className="space-y-0.5">
            {!collapsed && section.key !== 'top' && (
              <p className="px-2.5 pb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-sidebar-foreground/55">
                {t(`layout.sections.${section.key}` as MessageKey)}
              </p>
            )}
            {collapsed && section.key !== 'top' && <div className="mx-2 mb-1 h-px bg-sidebar-border" />}
            {section.entries.map(({ item, group }) =>
              group ? (
                <NavGroup
                  key={item.key}
                  item={item}
                  collapsed={collapsed}
                  active={item.to === activeTo || !!item.children?.some((c) => c.to === activeTo)}
                  badges={badges}
                  onNavigate={onNavigate}
                />
              ) : (
                <NavEntry key={item.key} item={item} collapsed={collapsed} active={item.to === activeTo} badge={badges[item.key] ?? 0} onNavigate={onNavigate} />
              ),
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}

/** Rozwijana grupa: zapamiętany stan (per urządzenie), sama się otwiera, gdy wchodzisz na jej stronę; licznik = suma podmenu. */
function NavGroup({
  item,
  collapsed,
  active,
  badges,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  badges: Record<string, number>;
  onNavigate?: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const open = useUiStore((s) => s.openNavGroups.includes(item.key));
  const setOpen = useUiStore((s) => s.setNavGroupOpen);
  const children = item.children ?? [];
  const badge = children.reduce((sum, c) => sum + (badges[c.key] ?? 0), 0);

  useEffect(() => {
    if (active) setOpen(item.key, true);
  }, [active, item.key, setOpen]);

  if (collapsed) {
    return (
      <DropdownMenu>
        <Tooltip content={t(item.labelKey)} side="right">
          <DropdownMenuTrigger asChild aria-label={t(item.labelKey)}>
            <span className="block">
              <NavEntry item={item} collapsed active={active} badge={badge} asButton />
            </span>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent side="right" align="start" sideOffset={10}>
          <DropdownMenuLabel>{t(item.labelKey)}</DropdownMenuLabel>
          {children.map((c) => (
            <DropdownMenuItem key={c.key} onSelect={() => navigate(c.to)} className={cn(pathname === c.to && 'bg-accent')}>
              <span className="flex-1">{t(c.labelKey)}</span>
              {(badges[c.key] ?? 0) > 0 && <span className="text-xs font-bold tabular-nums text-danger">{badges[c.key]}</span>}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div>
      <NavEntry
        item={item}
        collapsed={false}
        active={active && !open}
        badge={open ? 0 : badge}
        asButton
        expanded={open}
        onToggle={() => setOpen(item.key, !open)}
      />
      {open && (
        <div className="relative ml-[22px] mt-0.5 space-y-0.5 border-l border-sidebar-border pl-3">
          {children.map((c) => {
            const cc = c.color ?? accentForPath(c.to).color;
            const count = badges[c.key] ?? 0;
            return (
            <NavLink
              key={c.key}
              to={c.to}
              end={children.some((o) => o !== c && o.to.startsWith(`${c.to}/`))}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex h-8 items-center gap-2 rounded-lg px-2.5 text-[13px] transition-colors',
                  isActive ? 'font-semibold text-white' : 'text-sidebar-foreground hover:bg-white/[0.06] hover:text-white',
                )
              }
              style={({ isActive }) => (isActive ? { background: hexToRgba(cc, 0.16), boxShadow: `inset 0 0 0 1px ${hexToRgba(cc, 0.3)}` } : undefined)}
            >
              <span
                className="size-1.5 shrink-0 rounded-full"
                style={{ background: cc, opacity: pathname === c.to ? 1 : 0.7, boxShadow: pathname === c.to ? `0 0 8px ${hexToRgba(cc, 0.8)}` : undefined }}
              />
              <span className="flex-1 truncate">{t(c.labelKey)}</span>
              {count > 0 && (
                <span className="flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-black text-white">
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Desktop sidebar rail (hidden below `lg`; mobile uses the drawer from the topbar). */
export function Sidebar() {
  const t = useT();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);

  return (
    <aside
      className={cn(
        'dark sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 lg:flex',
        collapsed ? 'w-[68px]' : 'w-64',
      )}
    >
      <div className="min-h-0 flex-1">
        <SidebarContent collapsed={collapsed} />
      </div>
      <div className={cn('border-t border-sidebar-border p-2.5', collapsed && 'flex justify-center')}>
        <Tooltip content={collapsed ? t('nav.expand') : t('nav.collapse')} side="right">
          <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            className={cn(
              'flex h-8 items-center gap-2.5 rounded-xl px-2.5 text-[13px] font-medium text-sidebar-foreground transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
              collapsed ? 'w-8 justify-center px-0' : 'w-full',
            )}
          >
            {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
            {!collapsed && <span>{t('nav.collapse')}</span>}
          </button>
        </Tooltip>
        {!collapsed && (
          <p className="mt-1.5 px-2.5 text-[10.5px] tabular-nums text-sidebar-foreground/45" title={appBuildTitle()}>
            NEXOPS {appVersionLabel()}
          </p>
        )}
      </div>
    </aside>
  );
}
